import { motion } from "framer-motion";
import { ArrowLeft, Phone, MapPin, Clock, Star, ChefHat, Flame, Heart, ShoppingCart, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import ProjectTopBar from "@/components/ProjectTopBar";
import Seo from "@/components/Seo";

export default function Pizzeria() {
  const navigate = useNavigate(); const setLocation = (p) => navigate(p);
  const [currentPage, setCurrentPage] = useState("home");

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pizzas = [
    {
      name: "LA MARGHERITA",
      desc: "Tomate San Marzano, Mozzarella di Bufala, Basilic frais, Huile d'olive extra vierge",
      price: "12€",
      image: "https://images.unsplash.com/photo-1574071318508-1cdbab80d002?w=600&h=600&fit=crop&q=80"
    },
    {
      name: "LA REGINA",
      desc: "Tomate, Mozzarella, Jambon de Parme, Champignons frais, Olives noires",
      price: "14€",
      image: "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=600&h=600&fit=crop&q=80"
    },
    {
      name: "LA DIAVOLA",
      desc: "Tomate, Mozzarella, Salami piquant Calabrese, Piments frais, Origan",
      price: "14€",
      image: "https://images.unsplash.com/photo-1628840042765-356cda07504e?w=600&h=600&fit=crop&q=80"
    },
    {
      name: "LA QUATTRO FORMAGGI",
      desc: "Mozzarella, Gorgonzola DOP, Parmesan Reggiano, Fontina, Crème fraîche",
      price: "15€",
      image: "https://images.unsplash.com/photo-1513104890138-7c749659a591?w=600&h=600&fit=crop&q=80"
    },
    {
      name: "LA PROSCIUTTO E RUCOLA",
      desc: "Tomate, Mozzarella, Jambon cru 24 mois, Roquette fraîche, Copeaux de Parmesan",
      price: "16€",
      image: "https://images.unsplash.com/photo-1571997478779-2adcbbe9ab2f?w=600&h=600&fit=crop&q=80"
    },
    {
      name: "LA CRUDAIOLA",
      desc: "Tomate cerise, Burrata crémeuse, Prosciutto cru, Roquette, Huile de truffe",
      price: "18€",
      image: "https://images.unsplash.com/photo-1595854341625-f33ee10dbf94?w=600&h=600&fit=crop&q=80"
    },
  ];

  return (
    <>
      <Seo title="Bella Napoli — concept restaurant / pizzeria" description="Exemple de site pour un restaurant italien : menu, réservation et ambiance. Réalisation concept ML. Agence Web." />
      <ProjectTopBar />
      <div className="min-h-screen bg-white">
      {/* Checkerboard pattern background */}
      <div className="fixed inset-0 pointer-events-none opacity-5" style={{
        backgroundImage: `
          repeating-linear-gradient(0deg, transparent, transparent 20px, #dc2626 20px, #dc2626 21px),
          repeating-linear-gradient(90deg, transparent, transparent 20px, #dc2626 20px, #dc2626 21px)
        `
      }} />

      {/* Top Bar Rouge */}
      <div className="bg-gradient-to-r from-red-600 via-red-700 to-red-600 py-2 text-white text-center text-sm font-medium relative overflow-hidden">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(90deg, white 0px, white 10px, transparent 10px, transparent 20px)`,
          opacity: 0.1
        }} />
        <div className="relative">
          ☎️ Commandez maintenant : <strong className="font-black">01 42 78 33 45</strong> • Livraison Gratuite dès 20€
        </div>
      </div>

      {/* Navigation */}
      <nav className="bg-white border-b-4 border-red-700 sticky top-0 z-40 shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-4">
              <div className="text-5xl">🍕</div>
              <div>
                <h1 className="text-3xl font-black text-red-700 italic font-serif leading-none">
                  Bella
                </h1>
                <p className="text-xl font-black text-red-700 italic font-serif -mt-1">
                  Napoli
                </p>
              </div>
            </div>

            {/* Nav Links */}
            <div className="hidden md:flex items-center gap-8">
              {[
                { id: "home", label: "ACCUEIL" },
                { id: "menu", label: "NOS PIZZAS" },
                { id: "commande", label: "COMMANDE" },
                { id: "location", label: "OÙ NOUS TROUVER ?" },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => handlePageChange(item.id )}
                  className={`text-sm font-bold tracking-wider transition-colors uppercase ${
                    currentPage === item.id ? "text-red-700" : "text-gray-700 hover:text-red-600"
                  }`}
                >
                  {item.label}
                </button>
              ))}
              <Button 
                className="bg-red-700 hover:bg-red-800 text-white rounded-full px-6 font-bold uppercase"
                onClick={() => handlePageChange("contact")}
              >
                📧 CONTACT
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* HOME PAGE */}
      {currentPage === "home" && (
        <>
          {/* Hero with 3D Pizza */}
          <section className="relative min-h-[600px] bg-gradient-to-br from-red-600 via-red-700 to-red-800 overflow-hidden">
            {/* Background Image */}
            <div className="absolute inset-0 opacity-20">
              <img 
                src="https://images.unsplash.com/photo-1513104890138-7c749659a591?w=1920&h=1080&fit=crop&q=80" 
                alt="Pizza Background" 
                className="w-full h-full object-cover"
              />
            </div>
            {/* Checkerboard pattern */}
            <div className="absolute inset-0 opacity-10" style={{
              backgroundImage: `repeating-linear-gradient(45deg, transparent 0, transparent 20px, white 20px, white 22px)`
            }} />
            
            <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-12 items-center relative z-10">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="mb-6">
                  <span className="inline-block bg-white text-red-700 px-4 py-2 rounded-full font-black text-sm uppercase">
                    Authentique depuis 1982
                  </span>
                </div>
                <h1 className="text-4xl md:text-8xl font-black text-white mb-6 italic font-serif leading-none">
                  NOUVEAUTÉS
                </h1>
                <p className="text-xl md:text-3xl text-white/90 mb-8 font-medium">
                  La vraie pizza napolitaine<br />
                  Pâte fraîche • Four à bois
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    size="lg"
                    className="bg-white text-red-700 hover:bg-red-50 text-xl font-black px-10 h-16 rounded-full shadow-2xl"
                    onClick={() => handlePageChange("menu")}
                  >
                    DÉGUSTO MAINTENANT →
                  </Button>
                </div>
                <div className="mt-12 text-white/80 text-sm italic">
                  🏆 Élu "Meilleure Pizza de Paris" 2023
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ duration: 1, type: "spring" }}
                className="relative"
              >
                <div className="text-[5rem] md:text-[20rem] transform rotate-12 drop-shadow-2xl">
                  🍕
                </div>
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-yellow-400 text-black px-8 py-3 rounded-full font-black text-2xl shadow-2xl animate-bounce">
                  La Crudité!o
                </div>
              </motion.div>
            </div>
          </section>

          {/* About Section */}
          <section className="py-20 px-6 bg-gradient-to-b from-white to-red-50">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-16 items-center">
                <div>
                  <h2 className="text-5xl font-black text-red-700 mb-6 font-serif italic">
                    Notre Histoire
                  </h2>
                  <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                    Depuis 1982, **Bella Napoli** perpétue la tradition napolitaine avec passion. 
                    Notre secret ? Une pâte pétrie à la main chaque matin, une sauce tomate maison mijotée pendant des heures, 
                    et une cuisson au four à bois à 480°C.
                  </p>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                    Dirigée par le chef **Giuseppe Rossi**, notre équipe de pizzaïolos authentiques vous accueille 
                    dans une ambiance chaleureuse, comme à Naples !
                  </p>
                  <div className="flex gap-6">
                    <div className="text-center">
                      <div className="text-5xl mb-2">🏆</div>
                      <div className="font-black text-red-700">Prix 2023</div>
                      <div className="text-sm text-gray-600">Meilleure Pizza</div>
                    </div>
                    <div className="text-center">
                      <div className="text-5xl mb-2">👨‍🍳</div>
                      <div className="font-black text-red-700">Giuseppe</div>
                      <div className="text-sm text-gray-600">Chef Italien</div>
                    </div>
                    <div className="text-center">
                      <div className="text-5xl mb-2">🔥</div>
                      <div className="font-black text-red-700">Four à bois</div>
                      <div className="text-sm text-gray-600">480°C</div>
                    </div>
                  </div>
                </div>
                <div className="relative">
                  <img 
                    src="https://images.unsplash.com/photo-1574126154517-d1e0d89ef734?w=800&h=800&fit=crop&q=80" 
                    alt="Pizzeria Restaurant" 
                    className="w-full aspect-square object-cover rounded-3xl shadow-2xl"
                  />
                  <div className="absolute -bottom-6 -right-6 bg-yellow-500 rounded-full w-32 h-32 flex items-center justify-center text-5xl shadow-2xl">
                    🧀
                  </div>
                  <div className="absolute -top-6 -left-6 bg-red-600 rounded-full w-24 h-24 flex items-center justify-center text-4xl shadow-2xl">
                    🍅
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="py-16 px-6 bg-red-700">
            <div className="max-w-7xl mx-auto">
              <div className="grid md:grid-cols-4 gap-8">
                {[
                  { icon: <ChefHat className="w-10 h-10" />, title: "Fait Maison", desc: "Pâte fraîche quotidienne" },
                  { icon: <Flame className="w-10 h-10" />, title: "Four à Bois", desc: "Cuisson à 480°C" },
                  { icon: "🇮🇹", title: "100% Italien", desc: "Produits importés" },
                  { icon: "🚀", title: "Livraison Rapide", desc: "30 min chrono" },
                ].map((item, i) => (
                  <Card key={i} className="p-6 text-center bg-white border-2 border-red-200 hover:border-red-400 transition-all">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center text-red-700">
                      {typeof item.icon === 'string' ? <span className="text-3xl">{item.icon}</span> : item.icon}
                    </div>
                    <h3 className="font-black text-red-700 mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.desc}</p>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Reviews */}
          <section className="py-20 px-6 bg-white">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-5xl font-black text-red-700 text-center mb-12 font-serif italic">
                Ils nous adorent ❤️
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  { name: "Marie L.", text: "La meilleure pizza de Paris ! Pâte parfaite, ingrédients de qualité. On se croirait à Naples !", rating: 5 },
                  { name: "Thomas R.", text: "Accueil chaleureux, pizzas délicieuses. La Diavola est incroyable. Je recommande à 100% !", rating: 5 },
                  { name: "Sophie M.", text: "Un régal à chaque visite ! L'équipe est adorable et les pizzas sont authentiques.", rating: 5 },
                ].map((review, i) => (
                  <Card key={i} className="p-6 bg-red-50 border-l-4 border-red-700">
                    <div className="flex gap-1 mb-3">
                      {[...Array(review.rating)].map((_, j) => (
                        <Star key={j} className="w-5 h-5 fill-red-700 text-red-700" />
                      ))}
                    </div>
                    <p className="text-gray-700 mb-4 italic">"{review.text}"</p>
                    <p className="font-bold text-red-700">{review.name}</p>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* MENU PAGE */}
      {currentPage === "menu" && (
        <div className="py-20 px-6 bg-gradient-to-b from-white via-red-50 to-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-black text-red-700 text-center mb-4 font-serif italic">
              NOS PIZZAS
            </h2>
            <p className="text-center text-gray-600 mb-16 text-xl">
              Toutes nos pizzas sont préparées avec Mozzarella di Bufala et cuites au four à bois
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              {pizzas.map((pizza, i) => (
                <Card key={i} className="overflow-hidden bg-white border-2 border-red-200 hover:border-red-600 hover:shadow-2xl transition-all group">
                  <div className="aspect-video relative overflow-hidden">
                    <img 
                      src={pizza.image} 
                      alt={pizza.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-8">
                    <h3 className="text-2xl font-black text-red-700 mb-2 group-hover:text-red-600 transition-colors font-serif italic">
                      {pizza.name}
                    </h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {pizza.desc}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-4xl font-black text-red-700">{pizza.price}</span>
                      <Button 
                        size="sm" 
                        className="bg-red-700 hover:bg-red-800 rounded-full opacity-0 group-hover:opacity-100 transition-all"
                        onClick={() => handlePageChange("commande")}
                      >
                        <Heart className="w-4 h-4 mr-1" />
                        Commander
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <div className="text-center mt-16">
              <Button 
                size="lg"
                className="bg-red-700 hover:bg-red-800 text-white text-xl font-black px-12 h-16 rounded-full"
                onClick={() => handlePageChange("commande")}
              >
                <ShoppingCart className="w-6 h-6 mr-3" />
                Commander en ligne
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* COMMANDE PAGE */}
      {currentPage === "commande" && (
        <div className="py-20 px-6 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-black text-red-700 text-center mb-4 font-serif italic">
              COMMANDE EN LIGNE
            </h2>
            <p className="text-center text-gray-600 mb-12 text-xl">
              🍕 Livraison gratuite dès 20€ • ⏱️ Reçue en 30 minutes
            </p>
            
            <Card className="p-10 border-4 border-red-700 bg-white shadow-2xl">
              <form className="space-y-8" onSubmit={(e) => e.preventDefault()}>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-red-700 font-black mb-2 text-lg">Nom</label>
                    <Input className="border-2 border-red-200 focus:border-red-700 h-12 bg-white text-gray-900" />
                  </div>
                  <div>
                    <label className="block text-red-700 font-black mb-2 text-lg">Téléphone</label>
                    <Input type="tel" className="border-2 border-red-200 focus:border-red-700 h-12 bg-white text-gray-900" />
                  </div>
                </div>

                <div>
                  <label className="block text-red-700 font-black mb-2 text-lg">Adresse de livraison</label>
                  <Input className="border-2 border-red-200 focus:border-red-700 h-12 bg-white text-gray-900" placeholder="15 rue de..." />
                </div>

                <div>
                  <label className="block text-red-700 font-black mb-4 text-xl">Vos Pizzas</label>
                  <div className="space-y-4">
                    {pizzas.slice(0, 3).map((pizza, i) => (
                      <div key={i} className="flex items-center gap-4 p-4 border-2 border-red-200 rounded-xl hover:border-red-700 transition-colors bg-red-50">
                        <img src={pizza.image} alt={pizza.name} className="w-20 h-20 rounded-lg object-cover" />
                        <div className="flex-1">
                          <h4 className="font-black text-red-700">{pizza.name}</h4>
                          <p className="text-sm text-gray-600 line-clamp-1">{pizza.desc}</p>
                        </div>
                        <span className="text-2xl font-black text-red-700">{pizza.price}</span>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" className="border-red-700 text-red-700 hover:bg-red-700 hover:text-white">-</Button>
                          <span className="w-8 text-center font-bold">0</span>
                          <Button size="sm" className="bg-red-700 hover:bg-red-800">+</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button 
                    variant="outline" 
                    className="w-full mt-4 border-2 border-red-700 text-red-700 hover:bg-red-50 font-bold"
                    onClick={() => handlePageChange("menu")}
                  >
                    + Voir toutes nos pizzas
                  </Button>
                </div>

                <div>
                  <label className="block text-red-700 font-black mb-2 text-lg">Instructions spéciales (optionnel)</label>
                  <Textarea 
                    rows={3} 
                    className="border-2 border-red-200 focus:border-red-700 bg-white text-gray-900"
                    placeholder="Ex: Sans oignons, cuisson bien cuite..."
                  />
                </div>

                <div className="bg-red-50 p-6 rounded-xl border-2 border-red-200">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-xl font-bold text-gray-700">Total</span>
                    <span className="text-4xl font-black text-red-700">0€</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-6">
                    ✅ Livraison gratuite • 💳 Paiement à la livraison
                  </p>
                  <Button className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:opacity-90 text-white text-2xl font-black py-8 rounded-full shadow-2xl">
                    <ShoppingCart className="w-6 h-6 mr-3" />
                    VALIDER MA COMMANDE
                  </Button>
                </div>
              </form>
            </Card>

            <div className="mt-12 text-center">
              <p className="text-gray-600 text-lg">
                ☎️ Préférez commander par téléphone ? <br />
                <a href="tel:0142783345" className="text-red-700 font-black text-2xl hover:underline">01 42 78 33 45</a>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* LOCATION PAGE */}
      {currentPage === "location" && (
        <div className="py-20 px-6 bg-gradient-to-b from-white to-red-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-black text-red-700 text-center mb-4 font-serif italic">
              OÙ NOUS TROUVER ?
            </h2>
            <p className="text-center text-gray-600 mb-16 text-xl">
              Venez déguster nos pizzas sur place dans une ambiance authentique 🇮🇹
            </p>
            
            {/* Map */}
            <div className="aspect-video rounded-3xl overflow-hidden border-4 border-red-700 shadow-2xl mb-12">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2624.2!2d2.3319!3d48.8698!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e66e2964e34c2d%3A0x8ddca9ee380ef7e0!2s15%20Rue%20de%20la%20Paix%2C%2075002%20Paris!5e0!3m2!1sfr!2sfr!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
              />
            </div>

            {/* Info Cards */}
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="p-8 text-center border-4 border-red-700 bg-white hover:shadow-2xl transition-all">
                <div className="w-20 h-20 bg-red-700 rounded-full flex items-center justify-center mx-auto mb-6">
                  <MapPin className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-black text-red-700 mb-4 font-serif italic">Adresse</h3>
                <p className="text-gray-700 text-lg leading-relaxed">
                  15 rue de la Paix<br />
                  75002 Paris<br />
                  France 🇫🇷
                </p>
              </Card>

              <Card className="p-8 text-center border-4 border-red-700 bg-white hover:shadow-2xl transition-all">
                <div className="w-20 h-20 bg-red-700 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Phone className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-black text-red-700 mb-4 font-serif italic">Téléphone</h3>
                <a href="tel:0142783345" className="text-gray-700 text-2xl font-black hover:text-red-700 transition-colors">
                  01 42 78 33 45
                </a>
                <p className="text-gray-600 mt-4">
                  📱 Commandes & Réservations
                </p>
              </Card>

              <Card className="p-8 text-center border-4 border-red-700 bg-white hover:shadow-2xl transition-all">
                <div className="w-20 h-20 bg-red-700 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Clock className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-black text-red-700 mb-4 font-serif italic">Horaires</h3>
                <p className="text-gray-700 text-lg leading-relaxed">
                  <strong>Lun - Dim</strong><br />
                  11h00 - 23h00<br />
                  <span className="text-red-700 font-black">Ouvert 7j/7 ! 🍕</span>
                </p>
              </Card>
            </div>

            {/* Access Info */}
            <Card className="mt-12 p-10 bg-gradient-to-br from-red-600 to-red-700 border-4 border-red-800 text-white">
              <h3 className="text-3xl font-black mb-6 text-center font-serif italic">Comment venir ?</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-xl font-black mb-4">🚇 En Métro</h4>
                  <p className="text-white/90 leading-relaxed">
                    • Ligne 3 : Opéra (5 min à pied)<br />
                    • Ligne 7/14 : Pyramides (7 min à pied)<br />
                    • RER A : Auber (8 min à pied)
                  </p>
                </div>
                <div>
                  <h4 className="text-xl font-black mb-4">🚗 En Voiture</h4>
                  <p className="text-white/90 leading-relaxed">
                    Parking Vendôme à 200m<br />
                    Parking Place de la Madeleine à 400m<br />
                    Zone de livraison disponible
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* CONTACT PAGE */}
      {currentPage === "contact" && (
        <div className="py-20 px-6 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-black text-red-700 text-center mb-4 font-serif italic">
              CONTACTEZ-NOUS
            </h2>
            <p className="text-center text-gray-600 mb-16 text-xl">
              Une question ? Une suggestion ? On vous répond rapidement ! 📧
            </p>
            
            <div className="grid md:grid-cols-[60%_40%] gap-12">
              {/* Form */}
              <Card className="p-10 border-4 border-red-700 bg-white shadow-2xl">
                <h3 className="text-3xl font-black text-red-700 mb-8 font-serif italic">Envoyez un message</h3>
                <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-red-700 font-bold mb-2">Nom</label>
                      <Input className="border-2 border-red-200 focus:border-red-700 h-12 bg-white text-gray-900" />
                    </div>
                    <div>
                      <label className="block text-red-700 font-bold mb-2">Prénom</label>
                      <Input className="border-2 border-red-200 focus:border-red-700 h-12 bg-white text-gray-900" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-red-700 font-bold mb-2">Email</label>
                    <Input type="email" className="border-2 border-red-200 focus:border-red-700 h-12 bg-white text-gray-900" />
                  </div>

                  <div>
                    <label className="block text-red-700 font-bold mb-2">Téléphone (optionnel)</label>
                    <Input type="tel" className="border-2 border-red-200 focus:border-red-700 h-12 bg-white text-gray-900" />
                  </div>

                  <div>
                    <label className="block text-red-700 font-bold mb-2">Sujet</label>
                    <select className="w-full border-2 border-red-200 focus:border-red-700 h-12 rounded-md px-3 bg-white text-gray-900">
                      <option>Réservation</option>
                      <option>Commande</option>
                      <option>Question</option>
                      <option>Réclamation</option>
                      <option>Autre</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-red-700 font-bold mb-2">Votre message</label>
                    <Textarea 
                      rows={5} 
                      className="border-2 border-red-200 focus:border-red-700 bg-white text-gray-900"
                      placeholder="Écrivez votre message ici..."
                    />
                  </div>

                  <Button className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:opacity-90 text-white text-xl font-black py-6 rounded-full shadow-xl">
                    <Mail className="w-5 h-5 mr-3" />
                    ENVOYER LE MESSAGE
                  </Button>
                </form>
              </Card>

              {/* Contact Info */}
              <div className="space-y-6">
                <Card className="p-8 border-4 border-red-700 bg-red-50">
                  <h3 className="text-2xl font-black text-red-700 mb-6 font-serif italic">Infos Rapides</h3>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <Phone className="w-6 h-6 text-red-700 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-black text-red-700 mb-1">Téléphone</p>
                        <a href="tel:0142783345" className="text-gray-700 text-xl font-bold hover:text-red-700">
                          01 42 78 33 45
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <Mail className="w-6 h-6 text-red-700 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-black text-red-700 mb-1">Email</p>
                        <a href="mailto:contact@bellanapoli.fr" className="text-gray-700 hover:text-red-700 break-all">
                          contact@bellanapoli.fr
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <MapPin className="w-6 h-6 text-red-700 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-black text-red-700 mb-1">Adresse</p>
                        <p className="text-gray-700">
                          15 rue de la Paix<br />
                          75002 Paris
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <Clock className="w-6 h-6 text-red-700 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-black text-red-700 mb-1">Horaires</p>
                        <p className="text-gray-700">
                          Lun - Dim : 11h - 23h<br />
                          <span className="text-red-700 font-bold">Ouvert 7j/7</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card className="p-8 bg-gradient-to-br from-red-600 to-red-700 text-white border-4 border-red-800">
                  <h3 className="text-2xl font-black mb-4 font-serif italic">Temps de réponse</h3>
                  <p className="text-white/90 leading-relaxed">
                    ⚡ <strong>Moins de 2h</strong> en moyenne<br />
                    📧 Email : 24h max<br />
                    📞 Téléphone : immédiat !
                  </p>
                </Card>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA FINAL */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-red-600 via-red-700 to-red-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `repeating-linear-gradient(45deg, transparent 0, transparent 20px, white 20px, white 22px)`
        }} />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <div className="text-5xl md:text-8xl mb-8">🍕</div>
          <h2 className="text-4xl md:text-7xl font-black text-white mb-8 font-serif italic">
            Una Piccola Fame ?
          </h2>
          <p className="text-lg md:text-2xl text-white/90 mb-12">
            Commandez maintenant et savourez l'authentique pizza italienne !
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:0142783345" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto bg-white text-red-700 hover:bg-red-50 text-lg md:text-2xl font-black px-8 md:px-12 h-16 md:h-20 rounded-full">
                <Phone className="w-6 h-6 mr-3 shrink-0" />
                01 42 78 33 45
              </Button>
            </a>
            <Button
              size="lg"
              variant="outline"
              className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-red-700 text-lg md:text-2xl font-black px-8 md:px-12 h-16 md:h-20 rounded-full"
              onClick={() => handlePageChange("location")}
            >
              <MapPin className="w-6 h-6 mr-3 shrink-0" />
              Nous trouver
            </Button>
          </div>
          <div className="mt-16 text-white/80 text-lg">
            <p>📍 15 rue de la Paix, Paris 2ème</p>
            <p>🕐 Ouvert 7j/7 • 11h - 23h</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-black text-white text-center">
        <p>© 2024 Bella Napoli. Made with ❤️ & 🍕 by ML</p>
      </footer>
    </div>
    </>
  );
}

